import React, { useState, useEffect } from 'react';
import adminService from '../../services/adminService';
import styles from './AdminCategoriesPage.module.scss';
import commonStyles from '../AdminOrdersPage/AdminOrdersPage.module.scss';
import { IoTrash, IoAdd, IoPencil, IoImageOutline, IoClose, IoRefreshOutline, IoSaveOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import MediaLibraryModal from '../../components/common/MediaLibraryModal/MediaLibraryModal';

const BACKEND_URL = 'https://huongtramcomoc.vn';

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // FORM STATE
  const [id, setId] = useState(''); // ID Mới (hoặc ID đang nhập)
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  
  // EDIT STATE
  const [isEditing, setIsEditing] = useState(false);
  const [originalId, setOriginalId] = useState(''); // Lưu ID gốc khi sửa

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = () => {
    setLoading(true);
    adminService.getCategories()
      .then(response => {
        setCategories(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  };

  // --- LOGIC FORM ---

  // Tạo ID tự động
  const handleGenerateId = () => {
    const randomCode = "CAT" + Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    setId(randomCode);
  };

  // Tự động tạo slug khi nhập tên (nếu đang thêm mới)
  const handleNameChange = (e) => {
    const val = e.target.value;
    setName(val);
    if (!isEditing) {
        // Simple slugify
        const slugVal = val.toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/đ/g, "d").replace(/Đ/g, "D")
            .replace(/[^a-z0-9\s-]/g, "")
            .trim().replace(/\s+/g, "-");
        setSlug(slugVal);
    }
  };

  // Xử lý chọn ảnh
  const handleFileSelect = (file) => {
    setImageUrl(file.file_path);
  };

  // Bấm nút Sửa
  const handleEdit = (category) => {
    setIsEditing(true);
    setOriginalId(category.id); // Lưu ID cũ
    
    setId(category.id);
    setName(category.name);
    setSlug(category.slug);
    setImageUrl(category.image_url || '');
    
    // Scroll lên form
    const formElement = document.getElementById('category-form');
    if (formElement) formElement.scrollIntoView({ behavior: 'smooth' });
  };

  // Hủy/Reset Form
  const resetForm = () => {
    setIsEditing(false);
    setOriginalId('');
    setId('');
    setName('');
    setSlug('');
    setImageUrl('');
  };

  // SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!id || !name || !slug) {
        toast.warning("Vui lòng nhập đầy đủ Mã, Tên và Slug.");
        return;
    }

    const data = { id, name, slug, image_url: imageUrl };

    try {
      if (isEditing) {
        // Update: Gửi kèm old_id để backend tìm bản ghi cũ
        await adminService.updateCategory({ ...data, old_id: originalId });
        toast.success('Cập nhật thành công!');
      } else {
        // Create
        await adminService.createCategory(data);
        toast.success('Tạo danh mục thành công!');
      }
      resetForm();
      loadCategories();
    } catch (err) {
      toast.error('Lỗi: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = (catId) => {
    if (window.confirm(`Xóa danh mục "${catId}"? Lưu ý: Các sản phẩm thuộc danh mục này sẽ bị mất liên kết.`)) {
      adminService.deleteCategory(catId)
        .then(() => {
          toast.success('Đã xóa.');
          loadCategories();
        })
        .catch(err => toast.error('Lỗi xóa: ' + err.response?.data?.message));
    }
  };

  return (
    <div className={commonStyles.adminPage}>
      <h1>Quản Lý Danh Mục</h1>

      <div className={styles.layoutContainer}>
        
        {/* CỘT TRÁI: FORM NHẬP LIỆU */}
        <div className={styles.formColumn} id="category-form">
            <div className={styles.formCard}>
                <div className={styles.cardHeader}>
                    <h3>{isEditing ? `Sửa Danh Mục` : 'Thêm Mới'}</h3>
                    {isEditing && (
                        <button type="button" onClick={resetForm} className={styles.cancelBtn} title="Hủy bỏ">
                            <IoClose />
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    {/* ID & GEN BUTTON */}
                    <div className={styles.formGroup}>
                        <label>Mã danh mục (ID) <span className={styles.required}>*</span></label>
                        <div className={styles.inputGroupWithBtn}>
                            <input 
                                type="text" 
                                value={id}
                                onChange={(e) => setId(e.target.value)}
                                placeholder="VD: CAT001"
                                className={styles.inputControl}
                            />
                            {!isEditing && (
                                <button type="button" onClick={handleGenerateId} className={styles.iconBtn} title="Tạo mã tự động">
                                    <IoRefreshOutline />
                                </button>
                            )}
                        </div>
                        {isEditing && <small className={styles.hint}>Đổi mã ID sẽ cập nhật tự động cho các sản phẩm liên quan.</small>}
                    </div>

                    <div className={styles.formGroup}>
                        <label>Tên danh mục <span className={styles.required}>*</span></label>
                        <input 
                            type="text" 
                            value={name}
                            onChange={handleNameChange}
                            placeholder="VD: Trầm Hương"
                            className={styles.inputControl}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Slug (URL) <span className={styles.required}>*</span></label>
                        <input 
                            type="text" 
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            placeholder="vd: tram-huong"
                            className={styles.inputControl}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Hình ảnh</label>
                        <div className={styles.imagePicker}>
                            {imageUrl ? (
                                <div className={styles.previewBox}>
                                    <img src={`${BACKEND_URL}${imageUrl}`} alt="Preview" />
                                    <button type="button" onClick={() => setImageUrl('')} className={styles.removeImgBtn}><IoTrash /></button>
                                </div>
                            ) : (
                                <div className={styles.placeholderBox} onClick={() => setIsModalOpen(true)}>
                                    <IoImageOutline />
                                    <span>Chọn ảnh</span>
                                </div>
                            )}
                            {imageUrl && (
                                <button type="button" onClick={() => setIsModalOpen(true)} className={styles.changeImgBtn}>Đổi ảnh khác</button>
                            )}
                        </div>
                    </div>

                    <button type="submit" className={styles.submitBtn}>
                        {isEditing ? <IoSaveOutline /> : <IoAdd />}
                        {isEditing ? 'Lưu Thay Đổi' : 'Thêm Danh Mục'}
                    </button>
                </form>
            </div>
        </div>

        {/* CỘT PHẢI: DANH SÁCH */}
        <div className={styles.listColumn}>
            <div className={styles.listCard}>
                <h3>Danh sách hiện có ({categories.length})</h3>
                {loading ? <p className={styles.loadingText}>Đang tải dữ liệu...</p> : (
                    <div className={styles.tableResponsive}>
                        <table className={styles.customTable}>
                            <thead>
                                <tr>
                                    <th width="60">Ảnh</th>
                                    <th>Thông tin</th>
                                    <th width="80" className="text-center">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.length === 0 && (
                                    <tr><td colspan="3" className="text-center">Chưa có danh mục nào.</td></tr>
                                )}
                                {categories.map(cat => (
                                    <tr key={cat.id} className={originalId === cat.id ? styles.activeRow : ''}>
                                        <td>
                                            <div className={styles.imgCell}>
                                                {cat.image_url ? (
                                                    <img src={`${BACKEND_URL}${cat.image_url}`} alt={cat.name} />
                                                ) : (
                                                    <div className={styles.noImg}>-</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.infoCell}>
                                                <strong>{cat.name}</strong>
                                                <small>ID: <span className={styles.codeBadge}>{cat.id}</span></small>
                                                <small className={styles.slugText}>/{cat.slug}</small>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.actionCell}>
                                                <button onClick={() => handleEdit(cat)} className={styles.btnEdit} title="Sửa"><IoPencil /></button>
                                                <button onClick={() => handleDelete(cat.id)} className={styles.btnDel} title="Xóa"><IoTrash /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>

      </div>

      <MediaLibraryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onFileSelect={handleFileSelect}
      />
    </div>
  );
};

export default AdminCategoriesPage;